import React from 'react';
import { Alert, Badge, Button, Card, Col, Form, Pagination, Row, Tab, Table, Tabs } from 'react-bootstrap';
import { LinkContainer } from 'react-router-bootstrap';
import { useTypedProjectCostContext } from './useTypedProjectCostContext';
import { formatCurrencyFromCents, formatHours, formatPercent, phaseVariant, truncateText } from './utils';

const ProjectsCostAnalysisPage: React.FC = () => {
  const ctx = useTypedProjectCostContext<Record<string, any>>();
  const {
    analysisControlTab, setAnalysisControlTab, jqlInput, setJqlInput, handleCalculate, calculating,
    worklogStartDateInput, setWorklogStartDateInput, worklogEndDateInput, setWorklogEndDateInput,
    calcMaxResultsInput, setCalcMaxResultsInput, unlimitedIssues, setUnlimitedIssues,
    includeSubtaskWorklogs, setIncludeSubtaskWorklogs,
    nlQuestion, setNlQuestion, handleAskNl, nlLoading, nlError, nlResult, calcResult,
    executiveTotals, executiveCoverageThresholdPct, setExecutiveCoverageThresholdPct,
    criticalMarginPctThreshold, setCriticalMarginPctThreshold, executiveIssueRows, coverageSummary,
    previousAnalysis, calcIssuesLimit, setShowDetailedAnalysis, showDetailedAnalysis, topCompanyByCost,
    pagedAnalysisCollaborators, analysisCollaboratorsPageSize, setAnalysisCollaboratorsPageSize,
    setAnalysisCollaboratorsPage, pagedAnalysisIssues, analysisIssuesPageSize, setAnalysisIssuesPageSize,
    setAnalysisIssuesPage, showObservationColumn, setShowObservationColumn, openIssueObservationModal,
  } = ctx;

  return (              <Card>
                <Card.Header>Análise de custos via Jira</Card.Header>
                <Card.Body>
                  <Card className="mb-3">
                    <Card.Body className="py-2">
                      <strong>Como ler esta tela (resumo rápido)</strong>
                      <ol className="mb-0 mt-2 ps-3">
                        <li>Você roda a JQL para trazer as issues e worklogs do Jira.</li>
                        <li>Veja <strong>Custo por empresa</strong> para saber quanto cada empresa está gastando.</li>
                        <li>Veja <strong>Custo x Receita por issue</strong> para entender onde OS/AS impacta.</li>
                        <li>Veja <strong>Custo por colaborador</strong> para saber a taxa usada e de onde ela veio.</li>
                      </ol>
                      <small className="text-muted d-block mt-2">
                        Dica prática: se o resultado vier estranho, confira primeiro o card "Confiabilidade do cálculo" e as pendências de mapeamento.
                      </small>
                    </Card.Body>
                  </Card>

                  <Tabs
                    id="projects-costs-controls-tabs"
                    activeKey={analysisControlTab}
                    onSelect={(key) => setAnalysisControlTab((key as 'jql' | 'ai') || 'jql')}
                    className="mb-3"
                  >
                    <Tab eventKey="jql" title="JQL normal" />
                    <Tab eventKey="ai" title="Pesquisa IA" />
                  </Tabs>
                  {analysisControlTab === 'jql' ? (
                    <>
                      <Row className="g-2 align-items-end">
                        <Col lg={9}>
                          <Form.Group>
                            <Form.Label>JQL para cálculo</Form.Label>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              value={jqlInput}
                              onChange={(e) => setJqlInput(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Button className="w-100" onClick={handleCalculate} disabled={calculating}>
                            {calculating ? 'Calculando...' : 'Calcular'}
                          </Button>
                        </Col>
                      </Row>
                      <Row className="g-2 mt-1 align-items-end">
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label>Período inicial (worklog)</Form.Label>
                            <Form.Control
                              type="date"
                              value={worklogStartDateInput}
                              onChange={(e) => setWorklogStartDateInput(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col lg={3}>
                          <Form.Group>
                            <Form.Label>Período final (worklog)</Form.Label>
                            <Form.Control
                              type="date"
                              value={worklogEndDateInput}
                              onChange={(e) => setWorklogEndDateInput(e.target.value)}
                            />
                          </Form.Group>
                        </Col>
                        <Col lg={6} className="d-flex flex-wrap gap-2 align-items-end">
                          <Form.Group>
                            <Form.Label>Limite de issues</Form.Label>
                            <Form.Control
                              type="number"
                              min={1}
                              value={calcMaxResultsInput}
                              disabled={unlimitedIssues}
                              onChange={(e) => setCalcMaxResultsInput(e.target.value)}
                            />
                          </Form.Group>
                          <Form.Check
                            id="unlimited-issues"
                            className="mb-2"
                            label="Sem limite (todas as issues da JQL)"
                            checked={unlimitedIssues}
                            onChange={(e) => setUnlimitedIssues(e.target.checked)}
                          />
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const today = new Date().toISOString().slice(0, 10);
                              setWorklogStartDateInput('');
                              setWorklogEndDateInput(today);
                            }}
                          >
                            Total até hoje
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const today = new Date();
                              const end = today.toISOString().slice(0, 10);
                              const start = new Date(today);
                              start.setDate(today.getDate() - 29);
                              setWorklogStartDateInput(start.toISOString().slice(0, 10));
                              setWorklogEndDateInput(end);
                            }}
                          >
                            Últimos 30 dias
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              const today = new Date();
                              const end = today.toISOString().slice(0, 10);
                              const start = new Date(today.getFullYear(), today.getMonth(), 1);
                              setWorklogStartDateInput(start.toISOString().slice(0, 10));
                              setWorklogEndDateInput(end);
                            }}
                          >
                            Mês atual
                          </Button>
                          <Button
                            variant="outline-secondary"
                            onClick={() => {
                              setWorklogStartDateInput('');
                              setWorklogEndDateInput('');
                            }}
                          >
                            Limpar período
                          </Button>
                        </Col>
                        <Col lg={12}>
                          <Form.Check
                            id="include-subtask-worklogs"
                            label="Incluir horas apontadas em subtarefas das histórias"
                            checked={includeSubtaskWorklogs}
                            onChange={(e) => setIncludeSubtaskWorklogs(e.target.checked)}
                          />
                        </Col>
                      </Row>
                      {unlimitedIssues ? (
                        <Alert variant="warning" className="mt-2 mb-0 py-2">
                          Sem limite: o cálculo pode levar muitos minutos com histórico longo (ex.: 15 anos) e subtarefas ligadas.
                          Prefira filtrar por período de worklog quando possível.
                        </Alert>
                      ) : null}
                      <small className="text-muted d-block mt-2">
                        A taxa de OS/AS/OUTROS define a receita por hora da issue (conforme resumo do card no Jira). O custo por hora do colaborador
                        segue a prioridade: override do colaborador {'>'} regra empresa+função {'>'} função {'>'} empresa.
                      </small>
                    </>
                  ) : (
                    <Card className="mt-1 mb-2 border-0 bg-light-subtle">
                      <Card.Body className="py-2">
                        <Form.Group className="mb-2">
                          <Form.Label>Perguntar à IA (Jira + banco)</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={nlQuestion}
                            onChange={(e) => setNlQuestion(e.target.value)}
                            placeholder="Ex.: Quais projetos têm maior custo e quais riscos estão mais críticos?"
                          />
                        </Form.Group>
                        <div className="d-grid mb-2">
                          <Button variant="outline-primary" onClick={handleAskNl} disabled={nlLoading}>
                            {nlLoading ? 'Consultando...' : 'Perguntar à IA'}
                          </Button>
                        </div>
                        {nlError && <Alert variant="danger" className="py-2 mb-2">{nlError}</Alert>}
                        {nlResult && (
                          <>
                            <Alert variant="info" className="py-2 mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                              {nlResult.answer_text}
                            </Alert>
                            {nlResult.jira?.used_jql && (
                              <div className="small mb-2">
                                <div className="text-muted mb-1">JQL usada/sugerida:</div>
                                <code>{nlResult.jira.used_jql}</code>
                                <div className="d-grid mt-1">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    onClick={() => {
                                      setJqlInput(nlResult.jira?.used_jql || '');
                                      setAnalysisControlTab('jql');
                                    }}
                                  >
                                    Aplicar JQL e voltar para JQL normal
                                  </Button>
                                </div>
                              </div>
                            )}
                            {!!nlResult.warnings?.length && (
                              <Alert variant="warning" className="py-2 mb-0">
                                {nlResult.warnings.join(' | ')}
                              </Alert>
                            )}
                          </>
                        )}
                      </Card.Body>
                    </Card>
                  )}

                  {calcResult && (
                    <>
                      <small className="text-muted d-block mt-1">
                        Período aplicado no cálculo:{' '}
                        {calcResult.applied_worklog_start_date || 'início livre'} até {calcResult.applied_worklog_end_date || 'fim livre'}.
                      </small>
                      {executiveTotals && (
                        <Card className="mt-2 mb-2 cost-executive-card">
                          <Card.Body>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                              <strong>Portal de Compras — Receitas e Custos (visão executiva)</strong>
                              <div className="d-flex align-items-center gap-2">
                                <Form.Label className="small text-muted mb-0">Cobertura mínima (%)</Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={executiveCoverageThresholdPct}
                                  onChange={(e) => setExecutiveCoverageThresholdPct(Math.max(0, Math.min(100, Number(e.target.value || 0))))}
                                  style={{ width: 96 }}
                                />
                                <Form.Label className="small text-muted mb-0">Ponto crítico até (%)</Form.Label>
                                <Form.Control
                                  size="sm"
                                  type="number"
                                  min={0}
                                  max={100}
                                  value={criticalMarginPctThreshold}
                                  onChange={(e) => setCriticalMarginPctThreshold(Math.max(0, Math.min(100, Number(e.target.value || 0))))}
                                  style={{ width: 96 }}
                                />
                              </div>
                            </div>
                            <Row className="g-2">
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Venda total</div><strong>{formatCurrencyFromCents(executiveTotals.totalRevenueCents)}</strong></Card.Body></Card></Col>
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Custo próprio</div><strong>{formatCurrencyFromCents(executiveTotals.totalOwnCostCents)}</strong></Card.Body></Card></Col>
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Custo terceiro</div><strong>{formatCurrencyFromCents(executiveTotals.totalThirdPartyCostCents)}</strong></Card.Body></Card></Col>
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Custo total</div><strong>{formatCurrencyFromCents(executiveTotals.totalCostCents)}</strong></Card.Body></Card></Col>
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Margem pós-custo</div><strong>{formatCurrencyFromCents(executiveTotals.totalMarginCents)}</strong><small>{formatPercent(executiveTotals.totalMarginPct)}</small></Card.Body></Card></Col>
                              <Col md={2}><Card className="cost-kpi h-100"><Card.Body><div>Ponto crítico</div><strong>{executiveTotals.criticalIssues} issue(s)</strong><small>até {criticalMarginPctThreshold}%</small></Card.Body></Card></Col>
                            </Row>
                            <div className="small text-muted mt-2">
                              Em aprovação: <strong>{executiveTotals.approvalIssues}</strong> issue(s). A coluna “Margem na venda” do RAC depende de baseline comercial (HH vendido/custo de referência), não disponível integralmente no payload online do Jira.
                            </div>
                            <div className="table-responsive mt-2">
                              <Table size="sm" bordered hover>
                                <thead>
                                  <tr>
                                    <th>Projeto / Issue</th>
                                    <th>HH Real</th>
                                    <th>Valor de Venda</th>
                                    <th>Custo Próprio</th>
                                    <th>Custo Terceiro</th>
                                    <th>Custo Total</th>
                                    <th>Margem pós-custo</th>
                                    <th>%</th>
                                    <th>Fase</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {executiveIssueRows.slice(0, 20).map((row: any) => (
                                    <tr key={`executive-${row.issueKey}`}>
                                      <td>{row.issueKey}<div className="small text-muted">{truncateText(row.summary, 90)}</div></td>
                                      <td>{formatHours(row.hours)}</td>
                                      <td>{formatCurrencyFromCents(row.revenueCents)}</td>
                                      <td>{formatCurrencyFromCents(row.ownCostCents)}</td>
                                      <td>{formatCurrencyFromCents(row.thirdCostCents)}</td>
                                      <td>{formatCurrencyFromCents(row.totalCostCents)}</td>
                                      <td>{formatCurrencyFromCents(row.marginCents)}</td>
                                      <td>{formatPercent(row.marginPct)}</td>
                                      <td><Badge bg={phaseVariant(row.status)} className="fw-normal">{row.status || '—'}</Badge></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                            {executiveIssueRows.length > 20 && (
                              <small className="text-muted">Mostrando top 20 por valor de venda. Veja lista completa na seção detalhada de issues.</small>
                            )}
                          </Card.Body>
                        </Card>
                      )}
                      {coverageSummary && (
                        <Card className="mt-2 mb-2 cost-data-quality-card">
                          <Card.Body className="py-2">
                            <strong>Confiabilidade do cálculo</strong>
                            <Row className="g-2 mt-1">
                              <Col md={3}>
                                <div className="small text-muted">Issues calculadas</div>
                                <div className="fw-semibold">{coverageSummary.issuesCount}</div>
                              </Col>
                              <Col md={3}>
                                <div className="small text-muted">Horas mapeadas</div>
                                <div className="fw-semibold">{formatHours(coverageSummary.matchedHours)} h</div>
                              </Col>
                              <Col md={3}>
                                <div className="small text-muted">Horas não mapeadas</div>
                                <div className="fw-semibold">{formatHours(coverageSummary.unmatchedHours)} h</div>
                              </Col>
                              <Col md={3}>
                                <div className="small text-muted">Cobertura de mapeamento</div>
                                <div className="fw-semibold">{formatPercent(coverageSummary.mappingCoveragePercent)}</div>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      )}
                      {coverageSummary && coverageSummary.mappingCoveragePercent != null && coverageSummary.mappingCoveragePercent < executiveCoverageThresholdPct && (
                        <Alert variant="danger" className="py-2">
                          Cobertura de mapeamento ({formatPercent(coverageSummary.mappingCoveragePercent)}) abaixo do critério executivo mínimo ({executiveCoverageThresholdPct.toFixed(1)}%).
                          Ajuste o cadastro de colaboradores/Jira IDs antes de usar este resultado para reporte financeiro.
                        </Alert>
                      )}
                      {previousAnalysis && (
                        <Card className="mt-2 mb-2">
                          <Card.Body className="py-2">
                            <strong>Comparação rápida com a análise anterior</strong>
                            <div className="mt-1">
                              Custo: {formatCurrencyFromCents(calcResult.total_cost_cents - previousAnalysis.totalCostCents)}{' '}
                              | Receita: {formatCurrencyFromCents(calcResult.total_revenue_cents - previousAnalysis.totalRevenueCents)}{' '}
                              | Margem: {formatCurrencyFromCents(calcResult.total_margin_cents - previousAnalysis.totalMarginCents)}
                            </div>
                            <small className="text-muted">
                              Base de comparação: {new Date(previousAnalysis.createdAt).toLocaleString('pt-BR')} ({previousAnalysis.worklogStartDate || 'início livre'} até {previousAnalysis.worklogEndDate || 'fim livre'}).
                            </small>
                          </Card.Body>
                        </Card>
                      )}
                      {coverageSummary?.issueLimitReached && (
                        <Alert variant="warning" className="py-2">
                          O cálculo atingiu o limite de issues ({coverageSummary.issuesCount}/{calcIssuesLimit}). Pode haver itens fora desta análise.
                          Aumente o limite ou refine a JQL/período para garantir completude.
                        </Alert>
                      )}
                      {(coverageSummary?.unmatchedHours || 0) > 0 && (
                        <Alert variant="warning" className="py-2">
                          Existem {formatHours(coverageSummary?.unmatchedHours)}h sem colaborador mapeado. Isso reduz a confiabilidade do custo total.
                          <div className="small mt-1">
                            Estratégia recomendada: ir em <strong>Configurações</strong> e concluir vínculo `jira_account_id` dos autores pendentes.
                          </div>
                          <div className="d-flex flex-wrap gap-2 mt-2">
                            <LinkContainer to="/projects/costs/configuracoes">
                              <Button size="sm" variant="outline-warning">Ir para Configurações</Button>
                            </LinkContainer>
                            <Button size="sm" variant="outline-secondary" onClick={() => setShowDetailedAnalysis(true)}>
                              Ver pendências de mapeamento
                            </Button>
                          </div>
                        </Alert>
                      )}
                      <Card className="mt-2 mb-2 border-0 bg-light">
                        <Card.Body className="py-2">
                          <strong>Leitura simples (sem termos técnicos)</strong>
                          <div className="mt-1">
                            - Gasto total no período: <strong>{formatCurrencyFromCents(calcResult.total_cost_cents)}</strong>
                          </div>
                          <div>
                            - Receita total no período: <strong>{formatCurrencyFromCents(calcResult.total_revenue_cents)}</strong>
                          </div>
                          <div>
                            - Margem no período: <strong>{formatCurrencyFromCents(calcResult.total_margin_cents)}</strong>
                          </div>
                          <div>
                            - Empresa com maior gasto: <strong>{topCompanyByCost ? `${topCompanyByCost.company_name} (${formatCurrencyFromCents(topCompanyByCost.cost_cents)})` : '—'}</strong>
                          </div>
                        </Card.Body>
                      </Card>
                      {calcResult.total_cost_cents === 0 && calcResult.total_revenue_cents > 0 && (
                        <Alert variant="warning" className="py-2">
                          O custo ficou zerado, mas houve receita. Isso normalmente indica que os worklogs do Jira ainda não foram mapeados para colaboradores
                          cadastrados (Jira accountId/display name). Verifique o cadastro de colaboradores.
                        </Alert>
                      )}
                      <div className="d-flex justify-content-end mb-2">
                        <Button variant="outline-primary" size="sm" onClick={() => setShowDetailedAnalysis((prev: boolean) => !prev)}>
                          {showDetailedAnalysis ? 'Ocultar detalhes técnicos' : 'Mostrar detalhes técnicos'}
                        </Button>
                      </div>
                      <Row className="g-3 mt-2">
                        <Col md={3}><Card className="cost-kpi"><Card.Body><div>Total horas</div><strong>{formatHours(calcResult.total_hours)} h</strong></Card.Body></Card></Col>
                        <Col md={3}><Card className="cost-kpi"><Card.Body><div>Custo total</div><strong>{formatCurrencyFromCents(calcResult.total_cost_cents)}</strong></Card.Body></Card></Col>
                        <Col md={3}><Card className="cost-kpi"><Card.Body><div>Receita estimada</div><strong>{formatCurrencyFromCents(calcResult.total_revenue_cents)}</strong></Card.Body></Card></Col>
                        <Col md={3}><Card className="cost-kpi"><Card.Body><div>Margem estimada</div><strong>{formatCurrencyFromCents(calcResult.total_margin_cents)}</strong></Card.Body></Card></Col>
                      </Row>

                      {showDetailedAnalysis && (
                        <>
                          {calcResult.unmatched_worklogs.length > 0 && (
                            <Card className="mt-2 mb-2">
                              <Card.Body>
                                <h6 className="mb-2">Pendências de mapeamento (worklogs sem colaborador)</h6>
                                <div className="table-responsive">
                                  <Table size="sm" bordered hover>
                                    <thead>
                                      <tr>
                                        <th>Jira accountId</th>
                                        <th>Nome no Jira</th>
                                        <th>Horas</th>
                                        <th>Qtd. worklogs</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {calcResult.unmatched_worklogs.map((row: any, idx: number) => (
                                        <tr key={`${row.jira_account_id || 'sem-id'}-${idx}`}>
                                          <td>{row.jira_account_id || '—'}</td>
                                          <td>{row.jira_display_name}</td>
                                          <td>{formatHours(row.hours)}</td>
                                          <td>{row.worklog_count}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </Card.Body>
                            </Card>
                          )}
                          <Card className="mt-2 mb-2">
                            <Card.Body>
                              <h6 className="mb-2">Como o custo é calculado (regra oficial da tela)</h6>
                              <ol className="mb-0 ps-3">
                                <li>A tela lê issues e worklogs do Jira com a JQL + período informado.</li>
                                <li>Para cada worklog, identifica o colaborador por accountId (ou display name).</li>
                                <li>Define taxa do colaborador na ordem: override {'>'} empresa+função {'>'} função {'>'} empresa.</li>
                                <li>Calcula custo = horas * taxa do colaborador.</li>
                                <li>Calcula receita = horas * taxa de demanda (OS/AS/OUTROS da issue).</li>
                              </ol>
                            </Card.Body>
                          </Card>
                          <Row className="g-3 mt-1">
                            <Col lg={6}>
                              <h6>1) Quanto cada empresa está gastando</h6>
                              <div className="table-responsive">
                                <Table size="sm" bordered hover>
                                  <thead><tr><th>Empresa</th><th>Horas</th><th>Custo</th></tr></thead>
                                  <tbody>
                                    {calcResult.companies.map((row: any) => (
                                      <tr key={row.company_name}>
                                        <td>{row.company_name}</td>
                                        <td>{formatHours(row.hours)}</td>
                                        <td>{formatCurrencyFromCents(row.cost_cents)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Col>
                            <Col lg={6}>
                              <h6>2) Quanto cada colaborador custa (e qual taxa foi aplicada)</h6>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted">
                                  Mostrando {pagedAnalysisCollaborators.pageItems.length} de {pagedAnalysisCollaborators.totalItems} colaborador(es).
                                </small>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Select
                                    size="sm"
                                    value={String(analysisCollaboratorsPageSize)}
                                    onChange={(e) => {
                                      setAnalysisCollaboratorsPageSize(Number(e.target.value));
                                      setAnalysisCollaboratorsPage(1);
                                    }}
                                  >
                                    {[10, 20, 50].map((size) => (
                                      <option key={`analysis-collab-size-${size}`} value={size}>{size}/página</option>
                                    ))}
                                  </Form.Select>
                                  <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev
                                      onClick={() => setAnalysisCollaboratorsPage((prev: number) => Math.max(1, prev - 1))}
                                      disabled={pagedAnalysisCollaborators.currentPage <= 1}
                                    />
                                    <Pagination.Item active>
                                      {pagedAnalysisCollaborators.currentPage}/{pagedAnalysisCollaborators.totalPages}
                                    </Pagination.Item>
                                    <Pagination.Next
                                      onClick={() => setAnalysisCollaboratorsPage((prev: number) => Math.min(pagedAnalysisCollaborators.totalPages, prev + 1))}
                                      disabled={pagedAnalysisCollaborators.currentPage >= pagedAnalysisCollaborators.totalPages}
                                    />
                                  </Pagination>
                                </div>
                              </div>
                              <div className="table-responsive">
                                <Table size="sm" bordered hover>
                                  <thead><tr><th>Colaborador</th><th>Empresa</th><th>Origem da taxa</th><th>Taxa/h</th><th>Horas</th><th>Custo</th></tr></thead>
                                  <tbody>
                                    {pagedAnalysisCollaborators.pageItems.map((row: any) => (
                                      <tr key={`${row.collaborator_id}-${row.collaborator_name}`}>
                                        <td>{row.collaborator_name}</td>
                                        <td>{row.company_name}</td>
                                        <td>{row.effective_hourly_rate_source_label}</td>
                                        <td>{formatCurrencyFromCents(row.effective_hourly_rate_cents)}</td>
                                        <td>{formatHours(row.hours)}</td>
                                        <td>{formatCurrencyFromCents(row.cost_cents)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </Col>
                          </Row>

                          <Row className="g-3 mt-1">
                            <Col lg={12}>
                              <h6>3) Custo x Receita por issue (onde OS/AS entra)</h6>
                              <div className="cost-observation-config mb-2">
                                <Form.Check
                                  type="switch"
                                  id="toggle-issue-observation-column"
                                  label="Exibir coluna Observação"
                                  checked={showObservationColumn}
                                  onChange={(e) => setShowObservationColumn(e.target.checked)}
                                />
                                <small className="text-muted">
                                  Configuração focada apenas no campo Observação, como solicitado.
                                </small>
                              </div>
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <small className="text-muted">
                                  Mostrando {pagedAnalysisIssues.pageItems.length} de {pagedAnalysisIssues.totalItems} issue(s) calculada(s).
                                </small>
                                <div className="d-flex align-items-center gap-2">
                                  <Form.Select
                                    size="sm"
                                    value={String(analysisIssuesPageSize)}
                                    onChange={(e) => {
                                      setAnalysisIssuesPageSize(Number(e.target.value));
                                      setAnalysisIssuesPage(1);
                                    }}
                                  >
                                    {[15, 30, 50].map((size) => (
                                      <option key={`analysis-issue-size-${size}`} value={size}>{size}/página</option>
                                    ))}
                                  </Form.Select>
                                  <Pagination size="sm" className="mb-0">
                                    <Pagination.Prev
                                      onClick={() => setAnalysisIssuesPage((prev: number) => Math.max(1, prev - 1))}
                                      disabled={pagedAnalysisIssues.currentPage <= 1}
                                    />
                                    <Pagination.Item active>
                                      {pagedAnalysisIssues.currentPage}/{pagedAnalysisIssues.totalPages}
                                    </Pagination.Item>
                                    <Pagination.Next
                                      onClick={() => setAnalysisIssuesPage((prev: number) => Math.min(pagedAnalysisIssues.totalPages, prev + 1))}
                                      disabled={pagedAnalysisIssues.currentPage >= pagedAnalysisIssues.totalPages}
                                    />
                                  </Pagination>
                                </div>
                              </div>
                              <div className="table-responsive">
                                <Table size="sm" bordered hover>
                                  <thead>
                                    <tr>
                                      <th>Issue</th>
                                      <th>Resumo</th>
                                      {showObservationColumn && <th>Observação</th>}
                                      <th>Tipo (OS/AS...)</th>
                                      <th>Fase</th>
                                      <th>Horas</th>
                                      <th>Custo próprio</th>
                                      <th>Custo terceiro</th>
                                      <th>Custo</th>
                                      <th>Receita</th>
                                      <th>Margem</th>
                                      <th>Ações</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {pagedAnalysisIssues.pageItems.map((row: any) => (
                                      <tr key={row.issue_key}>
                                        <td>{row.issue_key}</td>
                                        <td title={row.issue_summary}>{truncateText(row.issue_summary, 90)}</td>
                                        {showObservationColumn && (
                                          <td title={row.issue_observation}>
                                            {truncateText(row.issue_observation || row.issue_summary, 90)}
                                            {row.has_custom_observation && (
                                              <Badge bg="info" className="ms-1">Editada</Badge>
                                            )}
                                          </td>
                                        )}
                                        <td>{row.demand_type}</td>
                                        <td>
                                          <Badge bg={phaseVariant(row.issue_status || '')} className="fw-normal">
                                            {row.issue_status || '—'}
                                          </Badge>
                                        </td>
                                        <td>{formatHours(row.hours)}</td>
                                        <td>{formatCurrencyFromCents(row.own_cost_cents)}</td>
                                        <td>{formatCurrencyFromCents(row.third_party_cost_cents)}</td>
                                        <td>{formatCurrencyFromCents(row.cost_cents)}</td>
                                        <td>{formatCurrencyFromCents(row.revenue_cents)}</td>
                                        <td>{formatCurrencyFromCents(row.revenue_cents - row.cost_cents)}</td>
                                        <td>
                                          <Button
                                            size="sm"
                                            variant="outline-primary"
                                            onClick={() => openIssueObservationModal(row.issue_key, row.issue_observation || row.issue_summary)}
                                          >
                                            Editar obs.
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                              <small className="text-muted">
                                Receita vem da taxa cadastrada em OS/AS/OUTROS. Custo vem da taxa aplicada ao colaborador (override, empresa+função, função ou empresa).
                              </small>
                            </Col>
                          </Row>
                        </>
                      )}
                    </>
                  )}
                </Card.Body>
              </Card>
  );
};

export default ProjectsCostAnalysisPage;
